import { weekStart, weekLabel, dayKey, isSubmitted, hours } from "../../admin-main/timesheet/timesheetWeek";

/**
 * One person's own timesheets.
 *
 * The manager's week answers "who has not submitted"; this answers "what did I
 * do, and is it in yet". The week arithmetic is shared with the manager's
 * screen deliberately — a week that starts on a different day for the person
 * filling it in than for the person approving it is a bug nobody would find
 * until the two totals disagreed.
 */
export { weekStart, weekLabel, hours };

const DAY_MS = 86400000;
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The sheet the screen opens on: the most recent week the person has. */
export const currentSheet = (sheets = []) => weeksOf(sheets)[0] || null;

export const weeksOf = (sheets = []) =>
  [...(sheets || [])]
    .filter((s) => s?.startDate)
    .sort((a, b) => String(weekStart(b.startDate)).localeCompare(String(weekStart(a.startDate))));

/**
 * The columns of the week grid.
 *
 * Monday to Friday by default, extended only if something was actually logged
 * at the weekend — a permanently empty Saturday column costs a seventh of the
 * grid's width to say nothing.
 */
export const weekDays = (sheet) => {
  const start = weekStart(sheet?.startDate);
  if (!start) return [];
  const base = Date.parse(`${start}T00:00:00.000Z`);
  const logged = new Set((sheet?.activities || []).map((a) => dayKey(a.date)).filter(Boolean));

  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const key = new Date(base + i * DAY_MS).toISOString().slice(0, 10);
    if (i >= 5 && !logged.has(key)) continue;
    days.push({ key, label: `${DOW[i]} ${Number(key.slice(8, 10))}` });
  }
  return days;
};

/**
 * The grid: one row per project, one column per day.
 *
 * Keyed on the project reference where there is one, so two spellings of the
 * same project do not become two rows — the same rule the manager's breakdown
 * uses.
 */
export const weekGrid = (sheet) => {
  const days = weekDays(sheet);
  const byProject = new Map();

  (sheet?.activities || []).forEach((a) => {
    const ref = a?.projectRef?._id || a?.projectRef;
    const name = a?.projectRef?.name || a?.project || "Internal and admin";
    const key = ref ? `ref:${String(ref)}` : `name:${name}`;
    const row = byProject.get(key) || { key, name, byDay: {}, total: 0 };
    const day = dayKey(a.date);
    const h = Number(a.hours) || 0;
    if (day) row.byDay[day] = (row.byDay[day] || 0) + h;
    row.total += h;
    byProject.set(key, row);
  });

  const rows = [...byProject.values()].sort((a, b) => b.total - a.total);
  const dailyTotals = {};
  days.forEach((d) => {
    dailyTotals[d.key] = rows.reduce((t, r) => t + (r.byDay[d.key] || 0), 0);
  });

  return { days, rows, dailyTotals, total: rows.reduce((t, r) => t + r.total, 0) };
};

/**
 * Hours logged in the calendar month a week belongs to, from submitted sheets
 * only — a draft is not a record of work, the same rule the manager's screen
 * applies to the whole cohort.
 */
export const monthTotals = (sheets = [], reference) => {
  const ref = dayKey(reference || new Date().toISOString());
  if (!ref) return { hours: 0, weeks: 0 };
  const month = ref.slice(0, 7);
  const inMonth = (sheets || []).filter(
    (s) => isSubmitted(s) && String(dayKey(s.startDate)).slice(0, 7) === month
  );
  return {
    hours: inMonth.reduce((t, s) => t + (Number(s.totalHours) || 0), 0),
    weeks: inMonth.length,
  };
};

export const awaitingApproval = (sheets = []) => (sheets || []).filter((s) => s?.status === "pending");

export const SHEET_STATUS = {
  incomplete: { label: "Draft", tone: "muted" },
  pending: { label: "Waiting on approval", tone: "warning" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Sent back", tone: "danger" },
};

export const sheetStatus = (sheet) => SHEET_STATUS[sheet?.status] || SHEET_STATUS.incomplete;

/** Only a draft or a sent-back week can still be edited and submitted. */
export const canSubmit = (sheet) =>
  Boolean(sheet) && (sheet.status === "incomplete" || sheet.status === "rejected");

/**
 * What to say about overtime.
 *
 * `overtimeHours` is computed on the server from the organisation's own
 * standard week, and `/timesheet/settings` is admin-only — so the figure is
 * quoted rather than recomputed, and the threshold is described as "the
 * standard week" rather than stated as a number we would be guessing at.
 */
export const overtimeNote = (sheet) => {
  const over = Number(sheet?.overtimeHours) || 0;
  if (over <= 0) return null;
  return `${hours(over)} hours above the standard week. Your approver sees this with the sheet.`;
};
